import { useGetAllExpedition } from "@/api/expedition.api";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import { useEffect, useMemo, useState } from "react";
import { ActionData } from "@/types/tableAction";
import { SortingState } from "@tanstack/react-table";
import usePagination from "@/hooks/useSyncedPagination";
import {
  validatedExpeditionsTableColumns,
  validatedExpeditionsTableColumnsMobile,
} from "@/components/validatedShipments/validatedExpeditionsTableColumns";
import { useValidatedExpeditionFilters } from "@/components/validatedShipments/useValidatedExpeditionFilters.reducer";
import { useDebounce } from "@/hooks/useDebounce";
import { FiltersValidatedExpeditions } from "@/components/validatedShipments/filtersValidatedExpeditions";
import { useGetUsers } from "@/api/user.api";
import { useGetCustoms } from "@/api/customs.api";
import { DeleteModal } from "@/components/validatedShipments/modals/delete.modal";
import { useNavigate } from "react-router-dom";
import { PostponeDialog } from "@/components/validatedShipments/postponeDialog";
import { ValidatedExpeditionMobileModal } from "@/components/validatedShipments/modals/validatedExpeditionMobileModal";
import { DetailModalClient } from "@/components/shared/detail.modal";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { toEndOfDayIso, toStartOfDayIso } from "@/lib/utils";

export const ValidatedShipmentsPage = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = usePagination("validees");
  const [actionData, setActionData] = useState<ActionData | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: "validatedAt", desc: true }]);

  const { filters, actions } = useValidatedExpeditionFilters();
  const debouncedFilters = useDebounce(filters, 300);

  const { data: customs } = useGetCustoms();
  const { data: users } = useGetUsers();
  const { data: expeditions } = useGetAllExpedition({
    processStatus: [
      ExpeditionProcessStatusEnum.VALIDATED,
      ExpeditionProcessStatusEnum.PENDING_PELICAN,
    ],
    deliveryStatus: debouncedFilters.deliveryStatus ? [debouncedFilters.deliveryStatus] : undefined,
    ...(debouncedFilters.expeditonType && {
      quotationType: debouncedFilters.expeditonType,
    }),
    search: debouncedFilters.search,
    country: debouncedFilters.country,
    createdById: debouncedFilters.userId,
    deliveryDateAfter: debouncedFilters.deliveryDateRange?.[0]
      ? toStartOfDayIso(debouncedFilters.deliveryDateRange[0])
      : undefined,
    deliveryDateBefore: debouncedFilters.deliveryDateRange?.[1]
      ? toEndOfDayIso(debouncedFilters.deliveryDateRange[1])
      : undefined,
    pickupDateAfter: debouncedFilters.pickupDateRange?.[0]
      ? toStartOfDayIso(debouncedFilters.pickupDateRange[0])
      : undefined,
    pickupDateBefore: debouncedFilters.pickupDateRange?.[1]
      ? toEndOfDayIso(debouncedFilters.pickupDateRange[1])
      : undefined,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id || "validatedAt",
    sortOrder: sorting[0]?.desc === true ? "DESC" : sorting[0]?.desc === false ? "ASC" : "DESC",
  });

  const expeditionsData = useMemo(() => {
    return expeditions?.data || [];
  }, [expeditions]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    debouncedFilters.search,
    debouncedFilters.country,
    debouncedFilters.expeditonType,
    debouncedFilters.userId,
    debouncedFilters.deliveryStatus,
    debouncedFilters.pickupDateRange,
    debouncedFilters.deliveryDateRange,
    setPagination,
  ]);

  return (
    <div className="flex h-fit w-full flex-1 flex-col">
      <div className="flex h-fit w-full flex-1 flex-col gap-8 p-4 md:p-8">
        <div className="flex flex-col items-start justify-between gap-2 md:flex-row md:items-center">
          <div className="flex flex-col gap-2">
            <h2 className="font-jakarta-sans text-3xl font-semibold">Expéditions validées</h2>
          </div>
        </div>
        <FiltersValidatedExpeditions
          filters={filters}
          actions={actions}
          customs={customs || []}
          users={users || []}
        />
        <div className="hidden w-full flex-col gap-10 md:flex">
          <DataTable
            columns={validatedExpeditionsTableColumns({
              setActionData,
              navigate,
            })}
            data={expeditionsData}
            rowCount={expeditions?.count || 0}
            setPagination={setPagination}
            pagination={pagination}
            sorting={sorting}
            setSorting={setSorting}
            enableMultiSort={false}
            autoResetPageIndex={false}
          />
        </div>
        <div className="flex w-full flex-col gap-10 md:hidden">
          <DataTable
            columns={validatedExpeditionsTableColumnsMobile({
              setActionData,
              navigate,
            })}
            data={expeditionsData}
            rowCount={expeditions?.count || 0}
            setPagination={setPagination}
            pagination={pagination}
            sorting={sorting}
            setSorting={setSorting}
            enableMultiSort={false}
            autoResetPageIndex={false}
          />
        </div>
      </div>
      {actionData && actionData.action === "DELETE" && (
        <DeleteModal
          expedition={actionData.expedition}
          onClose={() => setActionData(null)}
          open={true}
        />
      )}
      {actionData && actionData.action === "POSTPONE" && (
        <PostponeDialog expedition={actionData.expedition} onClose={() => setActionData(null)} />
      )}
      {actionData && actionData.action === "MOBILE" && (
        <ValidatedExpeditionMobileModal
          expedition={actionData.expedition}
          onClose={() => setActionData(null)}
          navigate={navigate}
          setActionData={setActionData}
        />
      )}
      {actionData && actionData.action === "DETAIL" && (
        <DetailModalClient
          expeditionId={actionData.expedition.id}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
    </div>
  );
};
